import { z } from 'zod';

export const ReserveContentItemSchema = z.object({
  id: z.string().guid(),
  organizationId: z.string().guid(),
  /** null = org-wide; set = hotel-specific. */
  hotelId: z.string().guid().nullable(),
  hotelName: z.string().nullable(),
  title: z.string(),
  bodyFb: z.string().nullable(),
  bodyIg: z.string().nullable(),
  hashtags: z.array(z.string()),
  category: z.string().nullable(),
  tags: z.array(z.string()),
  imageId: z.string().guid().nullable(),
  preApproved: z.boolean(),
  usageCount: z.number().int(),
  lastUsedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ReserveContentItem = z.infer<typeof ReserveContentItemSchema>;

export const CreateReserveContentSchema = z.object({
  /** null / omitted = org-wide. */
  hotelId: z.string().guid().nullable().optional(),
  title: z.string().min(1).max(280),
  bodyFb: z.string().max(8000).nullable().optional(),
  bodyIg: z.string().max(8000).nullable().optional(),
  hashtags: z.array(z.string().max(80)).max(50).default([]),
  category: z.string().max(40).nullable().optional(),
  tags: z.array(z.string().max(40)).max(20).default([]),
  imageId: z.string().guid().nullable().optional(),
  // `preApproved` is deliberately NOT accepted here or on update: it is the
  // bit that lets an inserted post skip the whole approval flow, so only the
  // server-side copy of a client-approved post (`createFromPost`) may set it.
  // Unknown keys are stripped by zod, so a client still sending it is ignored.
});
export type CreateReserveContent = z.infer<typeof CreateReserveContentSchema>;

export const UpdateReserveContentSchema = CreateReserveContentSchema.partial();
export type UpdateReserveContent = z.infer<typeof UpdateReserveContentSchema>;

/** Insert a reserve item into a plan as a new post slot. */
export const InsertReserveIntoPlanSchema = z.object({
  planId: z.string().guid(),
  scheduledFor: z.string().datetime(),
});
export type InsertReserveIntoPlan = z.infer<typeof InsertReserveIntoPlanSchema>;

export const ReserveContentQuerySchema = z.object({
  hotelId: z.string().guid().optional(),
  orgWide: z.coerce.boolean().optional(),
  preApproved: z.coerce.boolean().optional(),
  q: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
/** Validated/parsed query (limit + offset always present via defaults). */
export type ReserveContentQuery = z.infer<typeof ReserveContentQuerySchema>;
/** Raw input shape (limit + offset optional before defaults are applied). */
export type ReserveContentQueryInput = z.input<typeof ReserveContentQuerySchema>;
